import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';

import { Loader2, SquarePen } from 'lucide-react';
import { useIsMobile } from '@shadcn/ui/hooks/use-mobile';
import dayjs from 'dayjs';
import { GetContactAndConversationByIdQuery } from '@zaplify/graphql';
import { ActionDueNotification } from './due-notification';
import { useContactMessages } from '../../hooks/messaging/use-contact-messages';
import { Message } from '../../pages/actions/components/message/message';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui';
import { useMessageComposerV2 } from '../../hooks/messaging/message-composer/use-message-composer-v2';
import { ChannelType } from '../../types/channel-type';
import { AnimatePresence, motion } from 'motion/react';

interface NotificationProps {
    nextActionSuggestedAt?: string | null;
    snoozedUntil?: string | null;
    lastMessageSentAt?: Date | null;
    metadataLastUpdatedAt?: Date | null;
    onSchedule?: () => void;
    onRefreshData?: () => void;
    messageJustSent?: boolean;
}

// Custom hook to handle auto-scroll to bottom functionality
const useAutoScrollToBottom = (scrollRef: React.RefObject<HTMLDivElement>, dependencies: React.DependencyList) => {
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const scrollToBottom = useCallback(() => {
        if (scrollRef.current && shouldAutoScroll) {
            const element = scrollRef.current;
            element.scrollTop = element.scrollHeight - element.clientHeight;
            setIsScrolledToBottom(true);
        }
    }, [shouldAutoScroll]);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                const element = e.target as HTMLDivElement;
                const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
                const isAtBottom = scrollBottom <= 50;
                setIsScrolledToBottom(isAtBottom);

                // Stop auto-scrolling if user manually scrolls up
                if (!isAtBottom && shouldAutoScroll) {
                    setShouldAutoScroll(false);
                }
            }, 100);
        },
        [shouldAutoScroll]
    );

    // Auto-scroll when dependencies change (new contact, new messages)
    useEffect(() => {
        setShouldAutoScroll(true);
        // Use requestAnimationFrame for better timing with DOM updates
        const scrollAfterRender = () => {
            requestAnimationFrame(() => {
                scrollToBottom();
                // Additional scroll attempt after a short delay for async content
                setTimeout(scrollToBottom, 100);
            });
        };
        scrollAfterRender();
    }, dependencies);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return {
        isScrolledToBottom,
        handleScroll,
        scrollToBottom,
    };
};

export const ActionMessageListSkeleton: React.FC = () => {
    return (
        <div className="flex w-full justify-center p-4">
            <Loader2 className="h-10 w-10 animate-spin text-background-brand-primary" />
        </div>
    );
};

export const ActionMessageList: React.FC<{
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
    messages: GetContactAndConversationByIdQuery['Contacts_by_pk']['Messages'];
    contact: GetContactAndConversationByIdQuery['Contacts_by_pk'];
    notificationProps?: NotificationProps;
}> = ({ scrollAreaRef, onScroll, messages, contact, notificationProps }) => {
    const isMobile = useIsMobile();
    const { messagesNotesAndPendingMessages } = useContactMessages({ contactId: contact.id });
    const { channelType, handleChannelTypeChange } = useMessageComposerV2();

    const uniqueMessagesAndNotes = useMemo(() => {
        if (messagesNotesAndPendingMessages.length > 0) {
            return messagesNotesAndPendingMessages.filter(
                (message, index, self) => index === self.findIndex((t) => t.id === message.id)
            );
        }
        return messages.filter((message, index, self) => index === self.findIndex((t) => t.id === message.id));
    }, [messagesNotesAndPendingMessages, messages]);

    const { isScrolledToBottom, handleScroll: autoScrollHandler } = useAutoScrollToBottom(
        scrollAreaRef,
        [contact.id, uniqueMessagesAndNotes.length] // Auto-scroll when contact changes or messages update
    );

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            onScroll(e);
            autoScrollHandler(e);
        },
        [onScroll, autoScrollHandler]
    );

    return (
        <div
            className="flex flex-col gap-4 flex-1 p-4 overflow-auto animate-fade-in-0-4"
            ref={scrollAreaRef}
            onScroll={handleScroll}
        >
            {/* Div to push messages to the bottom */}
            <div className="flex flex-1" />
            {uniqueMessagesAndNotes?.map((message: any) => (
                <Message key={message.id} {...message} />
            ))}
            {contact?.conversationMetadata?.[0]?.metadata?.linkedinMessagesLastSyncedAt && isMobile && (
                <div className="flex justify-center flex-col items-center w-full text-sm text-text-tertiary">
                    <span>
                        LinkedIn messages last synced at{' '}
                        {dayjs(contact?.conversationMetadata?.[0]?.metadata?.linkedinMessagesLastSyncedAt).format(
                            'YYYY-MM-DD HH:mm'
                        )}
                    </span>
                    <span className="text-xs">Sync not supported on mobile</span>
                </div>
            )}

            <div className="flex justify-center py-2 gap-2 min-h-[38px]">
                <AnimatePresence mode="wait" initial={false}>
                    {channelType !== ChannelType.NOTE && (
                        <motion.div
                            key="add-note-badge"
                            layout
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{
                                duration: 0.2,
                                layout: { type: 'spring', stiffness: 300, damping: 30 },
                            }}
                        >
                            <Tooltip delayDuration={1000}>
                                <TooltipTrigger asChild>
                                    <Badge
                                        variant="outline"
                                        className="cursor-pointer flex gap-2 hover:bg-muted px-3 py-1.5"
                                        onClick={() => handleChannelTypeChange(ChannelType.NOTE)}
                                    >
                                        <SquarePen className="w-3 h-3" />
                                        <span>Add note</span>
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Add a note for yourself and the agent</p>
                                </TooltipContent>
                            </Tooltip>
                        </motion.div>
                    )}
                    {notificationProps && (
                        <motion.div
                            key="action-due-notification"
                            layout
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{
                                duration: 0.2,
                                layout: { type: 'spring', stiffness: 300, damping: 30 },
                            }}
                        >
                            <ActionDueNotification
                                nextActionSuggestedAt={notificationProps.nextActionSuggestedAt}
                                snoozedUntil={notificationProps.snoozedUntil}
                                lastMessageSentAt={notificationProps.lastMessageSentAt}
                                metadataLastUpdatedAt={notificationProps.metadataLastUpdatedAt}
                                onSchedule={notificationProps.onSchedule}
                                onRefreshData={notificationProps.onRefreshData}
                                messageJustSent={notificationProps.messageJustSent}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};
